import { selectUndoable } from "../../../helpers/selector.helpers"
import { getDescendantsOfSelected } from "../../../helpers/wrapper.helpers"
import { useDraggable } from "../../../hooks/editor/useDraggable"
import useHoldDrag, { dragNodeRefKey } from "../../../hooks/editor/useHoldDrag"
import useWrapSelectedObjects from "../../../hooks/editor/useWrapSelectedObjects"
import useDynamicRefs from "../../../hooks/useDynamicRefs"
import { useAppSelector, useAppStore } from "../../../hooks/useRedux"
import { CanvasCursor } from "../../../store/slices/canvasCursor.slice"
import { SELECTION_OVERLAY_REF_KEY } from "./SelectionOverlay"

type Props = {
    canvasScaleCoef: number
}

/**
 * Drag controller for a multiselection. Renders nothing.
 *
 * It used to render a box at the selection's bounding rectangle and MOVE THE SELECTED
 * OBJECTS INTO IT, because the drag was a single transform on that box and the objects had
 * to travel with it. The box sat at `z-index: 2500`, so every selected object was lifted
 * above the whole scene for as long as it was selected — select a rectangle that belongs
 * under a text layer and it would jump in front of it. Figma of course leaves the stacking
 * order alone.
 *
 * Re-parenting is no longer needed: `useHoldDrag` takes a LIST of drag nodes, so the same
 * transform can be written to each selected object exactly where it already is. The objects
 * keep their own `object.index`, there are no duplicated DOM ids to keep straight, and the
 * bounding box itself is drawn by `SelectionOverlay` (which derives it from the store, not
 * from this component).
 *
 * What is left is behaviour with no markup, so this stays a component rather than a hook:
 * the store subscriptions it needs live here instead of in `Canvas`, which is the root of
 * the whole object tree and must not re-render on a selection change.
 */
const ObjectsWrapper = ({ canvasScaleCoef }: Props) => {
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const canvasCursor = useAppSelector((state) => state.canvasCursor.value)
    const { selectedObjects } = useWrapSelectedObjects()
    const [getRef] = useDynamicRefs<HTMLElement>()
    const store = useAppStore()

    const { handleOnStartDrag, handleDragEnd, handleDragAxisChange, getSnapSession } = useDraggable(
        undefined,
        canvasScaleCoef,
        selectedObjects
    )

    useHoldDrag({
        enabled: canvasCursor !== CanvasCursor.GrabCursorBySpace,
        scale: (canvasScaleCoef * canvasZoom) / 100,
        /**
         * Every member moves on its own node, in its own place in the tree.
         *
         * A member whose ancestor is ALSO selected has to be left out: it already travels
         * with that ancestor's node, so transforming it as well would move it twice as far.
         * `handleDragEnd` skips the same objects when it commits, for the same reason.
         *
         * Read from the store on demand rather than subscribed — this runs once per drag.
         */
        getDragNodes: () => {
            const objectsById = selectUndoable(store.getState()).objects.value
            const ids = selectedObjects.map((object) => object.id)
            const carriedByAnAncestor = new Set(getDescendantsOfSelected(ids, objectsById))

            return ids
                .filter((id) => !carriedByAnAncestor.has(id))
                .map((id) => getRef(dragNodeRefKey(id))?.current ?? null)
        },
        // the frame would only get in the way of aligning the selection against something
        // else, so it goes away for the duration of the move (GE-1649)
        getNodesToHide: () => [getRef(SELECTION_OVERLAY_REF_KEY)?.current ?? null],
        /**
         * The multiselection moves when the press lands on one of its MEMBERS, not anywhere
         * inside its bounding rectangle — empty space in there belongs to whatever is
         * underneath, and has to stay ctrl-clickable.
         */
        isHit: (stack) =>
            stack.some((element) => selectedObjects.some((selected) => selected.id === element.id)),
        onDragStart: handleOnStartDrag,
        onAxisChange: handleDragAxisChange,
        getSnapSession,
        onDragEnd: handleDragEnd,
    })

    return null
}

export default ObjectsWrapper
